type SelectionItem = {
  id: number;
  name: string;
  sku?: string | null;
};

type SelectionState = {
  items: SelectionItem[];
  updatedAt: number;
};

const STORAGE_PREFIX = 'suite241_gestion_commerciale_product_assignment_selection_v1';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const hasStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const sanitizeKeyPart = (value: unknown, fallback: string): string => {
  const normalized = String(value ?? '').trim();

  return (normalized || fallback).replace(/[^a-zA-Z0-9_.-]/g, '_');
};

const resolveUserId = (store: unknown): string => {
  if (!isRecord(store)) return 'guest';

  const getters = isRecord(store.getters) ? store.getters : {};
  const state = isRecord(store.state) ? store.state : {};
  const authState = isRecord(state.auth) ? state.auth : {};
  const getterUser = getters['auth/currentUser'];
  const stateUser = authState.user;
  const user = isRecord(getterUser) ? getterUser : stateUser;

  return sanitizeKeyPart(isRecord(user) ? user.id : null, 'guest');
};

const resolveShopId = (store: unknown): string => {
  if (!isRecord(store)) return 'no-shop';

  const getters = isRecord(store.getters) ? store.getters : {};
  const state = isRecord(store.state) ? store.state : {};
  const shopState = isRecord(state.shop) ? state.shop : {};
  const shopId = getters['shop/currentShopId'] ?? shopState.currentShopId;

  return sanitizeKeyPart(shopId, 'no-shop');
};

const normalizeSelectionItem = (value: unknown): SelectionItem | null => {
  if (!isRecord(value)) return null;

  const id = Number(value.id);
  if (!Number.isFinite(id) || id < 1) return null;

  return {
    id: Math.floor(id),
    name: String(value.name || '').trim() || `Produit #${Math.floor(id)}`,
    sku: value.sku == null ? null : String(value.sku),
  };
};

export const productAssignmentSelectionKey = (
  targetType: string,
  targetId: unknown,
  store?: unknown
): string =>
  [
    STORAGE_PREFIX,
    resolveUserId(store),
    resolveShopId(store),
    sanitizeKeyPart(targetType, 'target'),
    sanitizeKeyPart(targetId, '0'),
  ].join(':');

export const loadProductAssignmentSelection = (
  targetType: string,
  targetId: unknown,
  store?: unknown
): SelectionItem[] => {
  if (!hasStorage()) return [];

  try {
    const raw = window.localStorage.getItem(productAssignmentSelectionKey(targetType, targetId, store));
    if (!raw) return [];

    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed) || !Array.isArray(parsed.items)) return [];

    const byId = new Map<number, SelectionItem>();
    parsed.items
      .map(normalizeSelectionItem)
      .filter((item): item is SelectionItem => Boolean(item))
      .forEach((item) => byId.set(item.id, item));

    return Array.from(byId.values());
  } catch (error) {
    console.warn('Sélection de produits illisible', error);
    return [];
  }
};

export const saveProductAssignmentSelection = (
  targetType: string,
  targetId: unknown,
  items: SelectionItem[],
  store?: unknown
): void => {
  if (!hasStorage()) return;

  const state: SelectionState = {
    items: items
      .map(normalizeSelectionItem)
      .filter((item): item is SelectionItem => Boolean(item)),
    updatedAt: Date.now(),
  };

  try {
    window.localStorage.setItem(
      productAssignmentSelectionKey(targetType, targetId, store),
      JSON.stringify(state)
    );
  } catch (error) {
    console.warn('Sélection de produits non enregistrée', error);
  }
};

export const clearProductAssignmentSelection = (
  targetType: string,
  targetId: unknown,
  store?: unknown
): void => {
  if (!hasStorage()) return;

  try {
    window.localStorage.removeItem(productAssignmentSelectionKey(targetType, targetId, store));
  } catch (error) {
    console.warn('Sélection de produits non supprimée', error);
  }
};
